<script lang="ts">
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import { cloneDeep } from "lodash";
  import { sortedDistributors } from "@retailer/services/distributor_service";
  import { saveDistributor } from "@retailer/svelte/ProductDetails/Api.svelte";
  import Flash from "@shared/services/flashv2.js";
  import ChatWithSupportLink from "@shared/svelte/Components/ChatWithSupportLink.svelte";
  import DefaultDistributorPromptModal from "@retailer/svelte/ProductDetails/Components/DefaultDistributorPromptModal.svelte";
  import type { ProductLine, PLDistributor } from "@shared/types/ProductLine";
  import type { Distributor } from "@shared/types/Distributor";

  export let distributors: Distributor[] = [];
  export let hasSelectedDistributor: boolean;
  export let productLine: ProductLine;
  export let hasProducts: boolean;
  export let distributor: PLDistributor;
  export let selectedDistributor: Distributor;
  export let alwaysChooseForceDistributorSelection: boolean = false;

  let oldDistributor: PLDistributor;
  let editing: boolean = false;
  let loading: boolean = false;
  let showDefaultDistributorPrompt: boolean = false;

  const edit = () => {
    oldDistributor = cloneDeep(distributor);
    distributor = null;
    editing = true;
  };

  const cancelEdit = () => {
    distributor = oldDistributor;
    editing = false;
  };

  const save = () => {
    loading = true;
    saveDistributor(productLine, selectedDistributor, successCallback, errorCallback);
  };

  const successCallback = res => {
    if (res.show_default_distributor_for_spirits) {
      showDefaultDistributorPrompt = true;
    } else reload();
  };

  const errorCallback = () => {
    loading = false;
    Flash.now({
      type: "error",
      message: `Sorry, we've had a problem. Please contact support`,
    });
  };

  const reload = () => {
    window["stateService"].reload();
  };

  const editable = () => {
    return (
      productLine.product_type_name === "Non-Alcoholic" ||
      productLine.product_type_name === "Non-Beverage" ||
      productLine.retailer_orders_from_class_b ||
      distributor?.state == "GA" ||
      (distributor || {}).always_choose_distributor
    );
  };

  const distributorSelection = sortedDistributors(distributors);
</script>

{#if !hasSelectedDistributor}
  <div class="choose-distributor-wrapper">
    <ul
      class="product-detail__product-tabs tabs-background background-brandSecondary list list--horizontal list--gutter-none align-center light-subheader--regular"
    >
      <li class="padding--smaller align-left fizz-space-between">
        <span class="fizz-flex-row-4">
          <Icon
            type="Truck"
            height="24"
            width="18"
            extraStyles="display: inline;"
            extraClassNames="svg-icon-truck margin-left--smaller margin-right--smallest vertical-align-middle color-white"
          />
          <p>Distributors</p>
          {#if editing}
            <button class="button button--label float-right button--header" on:click={cancelEdit}>
              Close
            </button>
          {/if}
        </span>
      </li>
      <li class="choose-distributor-picker background-white padding--small">
        <div class="default-subheader--bold align-left margin-bottom--smallest">
          Choose your Distributor
        </div>
        <div class="grid-row">
          <div class="grid-col c8-12">
            <div class="input-wrapper select-style full-width">
              {#if !loading}
                <select
                  bind:value={selectedDistributor}
                  class="fizz-text-body dark:fizz-text-inverse"
                  id="choose-distributor-select"
                >
                  <option>Select...</option>
                  {#each distributorSelection as distributor}
                    <option value={distributor}>{distributor.name}</option>
                  {/each}
                </select>
              {/if}
            </div>
          </div>
          <div class="grid-col c4-12">
            <button
              class="button button--primary full-width"
              on:click={save}
              disabled={loading || !selectedDistributor || !selectedDistributor.id}
            >
              Save
            </button>
          </div>
        </div>
        <div class="grid-row">
          <div class="grid-col c12-12">
            <div class="default-body--regular margin-top--small">
              Don't see your distributor?
              <ChatWithSupportLink message={"Chat with support"} brandprimary={true} />
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
{:else}
  <!-- Selected Distributor State -->
  <div class="choose-distributor-wrapper">
    <ul
      class="product-detail__product-tabs tabs-background list list--horizontal list--gutter-none align-center light-subheader--regular"
    >
      <li class="padding--smaller align-left">
        <Icon
          type="Truck"
          height="24"
          width="18"
          extraStyles="display: inline;"
          extraClassNames="svg-icon-truck margin-left--smaller margin-right--smallest vertical-align-middle color-white"
        />
        <span>Distributors</span>
      </li>
      <li class="choose-distributor-selected background-white padding--small">
        <div class="grid-row">
          <div class="grid-col c10-12">
            <div class="default-subheader--bold align-left ellipsis">
              {distributor.name}
            </div>
          </div>
          {#if editable()}
            <div class="grid-col c2-12">
              <button
                class="button button--secondary button--smallest float-right switch-distributor-button"
                on:click={edit}
              >
                Change
              </button>
            </div>
          {/if}
        </div>
        {#if alwaysChooseForceDistributorSelection && !hasProducts}
          <div class="grid-row">
            <div class="grid-col c12-12">
              <div class="s6 color-black align-left">Please choose a different distributor.</div>
            </div>
          </div>
        {/if}
      </li>
    </ul>
  </div>
{/if}

<DefaultDistributorPromptModal
  productTypeName={productLine.product_type_name}
  productTypeId={productLine.product_type_id}
  distributor={selectedDistributor}
  showModal={showDefaultDistributorPrompt}
  closeCallback={reload}
/>
